import React from 'react';
import { View, Text, StyleSheet, Platform, type ViewStyle, type TextStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';
import { useTranslation } from '../../../../shared/i18n';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { appTheme } from '../../../../shared/theme';
import { Clickable } from '../../../../components/common/Clickable';
import { HorizontalLtrScrollView } from '../../../../components/ui/HorizontalLtrScrollView';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import { DashboardCardShellSet2 } from './DashboardCardShellSet2';

const DEFAULT_NUM_COLUMNS = 9;
const DEFAULT_ITEM_GAP = appTheme.spacing.sm;
const DEFAULT_MAX_HEIGHT = 240;
const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export interface DashboardScrollableGridCardSet2Props {
  colors: ThemeColors;
  items: React.ReactNode[];
  on_container_press?: () => void;
  num_columns?: number;
  item_gap?: number;
  max_height?: number;
  title?: string;
  key_extractor?: (index: number) => string;
  style?: ViewStyle;
  content_container_style?: ViewStyle;
}

/** v2 grid — horizontal strip inside the studio card shell. */
export function DashboardScrollableGridCardSet2({
  colors,
  items,
  on_container_press,
  num_columns: _num_columns = DEFAULT_NUM_COLUMNS,
  item_gap: _item_gap = DEFAULT_ITEM_GAP,
  max_height = DEFAULT_MAX_HEIGHT,
  title,
  key_extractor: _key_extractor,
  style,
  content_container_style,
}: DashboardScrollableGridCardSet2Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const item_gap = _item_gap;
  const card_padding = DASH_STUDIO_INNER;

  return (
    <DashboardCardShellSet2 colors={colors} no_padding style={style}>
      {title != null && title !== '' ? (
        <View style={[styles.titleWrap, { paddingHorizontal: card_padding, paddingTop: card_padding, paddingBottom: appTheme.spacing.xs }]}>
          {on_container_press != null ? (
            <Clickable onPress={on_container_press} accessibilityLabel={title} accessibilityRole="button">
              <View style={styles.titleRow}>
                <Text
                  style={[styles.title, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                  numberOfLines={1}>
                  {title}
                </Text>
                <Text style={[styles.titleLink, { color: colors.primary }, ANDROID_TEXT]}>{t('dashboard.viewAll')}</Text>
              </View>
            </Clickable>
          ) : (
            <Text
              style={[styles.title, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
              numberOfLines={1}>
              {title}
            </Text>
          )}
        </View>
      ) : null}
      <HorizontalLtrScrollView
        style={[styles.scroll, { maxHeight: max_height || undefined }]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingVertical: appTheme.spacing.md,
            paddingHorizontal: card_padding,
            paddingBottom: card_padding,
            alignItems: 'flex-start',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: item_gap,
          },
          content_container_style,
        ]}
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        horizontal>
        {items}
      </HorizontalLtrScrollView>
    </DashboardCardShellSet2>
  );
}

const styles = StyleSheet.create({
  titleWrap: {},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    minHeight: 20,
  },
  title: {
    ...appTheme.typography.captionMini,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    flex: 1,
    minWidth: 0,
    fontWeight: '800',
    fontSize: 10,
  },
  titleLink: {
    fontSize: 12,
    fontWeight: '700',
    flexShrink: 0,
  },
  scroll: {
    margin: 0,
    padding: 0,
  },
  scrollContent: {},
});
